import React from 'react';
import {
    ArrowDown,
    ArrowUp,
    Columns3,
    EyeOff,
} from 'lucide-react';
import Menu, { MenuDivider, MenuItem } from '../../ui/Menu';
import { cx } from '../../ui/classNames';

// Sort item labels by the column's `meta.sortKind`.
const SORT_LABELS = {
    text: { asc: 'Sort A to Z', desc: 'Sort Z to A' },
    number: { asc: 'Sort low to high', desc: 'Sort high to low' },
    date: { asc: 'Sort oldest first', desc: 'Sort newest first' },
    marketStatus: { asc: 'Sort open first', desc: 'Sort open last' },
};
// The column menu's MUI look: right under the button, no shadow, 8px above
// and below the items, 14px item text (12px compact) with 16px side padding,
// disabled items faded rather than greyed, and MUI's list icon colour.
const MENU_CLASS_NAME = cx(
    'min-w-[250px] !px-0 !py-2 !text-[14px] !leading-[1.5] !shadow-none',
    '[[data-compact]_&]:!text-[12px]',
);
const ITEM_CLASS_NAME = '!min-h-0 !gap-0 !rounded-md !px-4 disabled:!text-ui-ink-soft disabled:opacity-[0.38]';
const ICON_CLASS_NAME = 'size-5 text-black/[0.54] dark:text-white [[data-compact]_&]:size-[17px]';

/**
 * A column's menu (a TanStack column), anchored to its header's menu button
 * (`anchorEl`, whose id names the menu): sort items, then Hide column /
 * Manage columns, separated by a divider. `menuId` is the id the button's
 * aria-controls names. The menu takes focus on open (ArrowDown reaches the
 * first item). Escape and Tab close it with `onClose({ restoreFocus: true })`,
 * which focuses the header; an outside press closes it where the pointer
 * went.
 */
export default function WatchlistGridColumnMenu({
    open,
    anchorEl,
    column,
    menuId,
    sortDirection = null,
    canHide = true,
    onSort,
    onHideColumn,
    onManageColumns,
    onClose,
}) {
    if (!anchorEl || !column) return null;
    const { meta } = column.columnDef;
    const sortLabels = SORT_LABELS[meta?.sortKind] || SORT_LABELS.text;
    const sortable = column.getCanSort();
    const hideable = column.getCanHide();
    const icon = (Icon) => <Icon aria-hidden="true" className={ICON_CLASS_NAME} />;

    const sortItems = sortable ? [
        sortDirection !== 'asc' ? (
            <MenuItem key="asc" icon={icon(ArrowUp)} className={ITEM_CLASS_NAME} onSelect={() => onSort('asc')}>
                {sortLabels.asc}
            </MenuItem>
        ) : null,
        sortDirection !== 'desc' ? (
            <MenuItem key="desc" icon={icon(ArrowDown)} className={ITEM_CLASS_NAME} onSelect={() => onSort('desc')}>
                {sortLabels.desc}
            </MenuItem>
        ) : null,
        sortDirection ? (
            <MenuItem key="clear" icon={null} className={ITEM_CLASS_NAME} onSelect={() => onSort(null)}>
                Clear sort
            </MenuItem>
        ) : null,
    ].filter(Boolean) : [];

    return (
        <Menu
            anchor={anchorEl}
            open={open}
            onOpenChange={(next, _event, reason) => {
                if (next) return;
                onClose({ restoreFocus: reason === 'escape-key' || reason === 'focus-out' });
            }}
            id={menuId}
            offset={0}
            returnFocus={false}
            autoFocusItem={false}
            placement={meta?.align === 'right' ? 'bottom-start' : 'bottom-end'}
            className={MENU_CLASS_NAME}
        >
            {sortItems}
            {sortItems.length > 0 && <MenuDivider />}
            {hideable && (
                <MenuItem
                    icon={icon(EyeOff)}
                    disabled={!canHide}
                    className={ITEM_CLASS_NAME}
                    onSelect={onHideColumn}
                >
                    Hide column
                </MenuItem>
            )}
            <MenuItem icon={icon(Columns3)} className={ITEM_CLASS_NAME} onSelect={onManageColumns}>
                Manage columns
            </MenuItem>
        </Menu>
    );
}
